import { createContext, useContext, useRef, useState, useEffect, useCallback } from 'react';
import * as Sentry from '@sentry/browser';
import { getSetting, setSetting, settingKeys } from '../utils/settings';
import auth from '../services/authService';
import userService from '../services/userService';
import songService from '../services/songService';
import serviceService from '../services/serviceService';
import { getCategories } from '../services/fakeCategoryService';
import { sortPastServices } from '../utils/utilities';
import { setNetworkErrorHandler, setLoaderErrorActive } from '../services/httpService';

const GlobalContext = createContext();

const ENABLE_SIGN_UP = false;

export const signUpEnabled = ENABLE_SIGN_UP && process.env.NODE_ENV === 'development';

export const GlobalProvider = ({ children }) => {
  const [showPlugins, setShowPlugins] = useState(getSetting(settingKeys.showPlugins) || false);
  const [canSuggestSongs, setCanSuggestSongs] = useState(false);

  // Auto-scroll Plugin
  const [isScrolling, setIsScrolling] = useState(false);
  const [scrollingSpeed, setScrollingSpeed] = useState(90); // Default to speed 4 (90ms)
  // Smart scroll's pace multiplier. Shared for the same reason scrollingSpeed is: the plugin, the
  // pinned service bar and the SuperScroll overlay are three views of one control, and a nudge in
  // any of them has to show up in the others straight away.
  const [scrollingPace, setScrollingPace] = useState(1);
  const scrollTimerRef = useRef(null);

  // Songs and Services
  const [songs, setSongs] = useState([]);
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [servicesLoaded, setServicesLoaded] = useState(false);
  const [connectionError, setConnectionError] = useState(false);
  const [currentServiceForm, setCurrentServiceForm] = useState({
    songs: [],
    newSongs: [],
    title: '',
    date: new Date(),
    editServiceId: null,
  });

  const user = auth.getCurrentUser();
  const [showRemovedFromTeamModal, setShowRemovedFromTeamModal] = useState(user?.teamId === null);

  // Welcome modal
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [welcomeModalData, setWelcomeModalData] = useState({
    firstName: '',
    teamName: '',
    teamMembers: [],
    isAdmin: false,
  });

  const [allThemes, setAllThemes] = useState([]);

  // Fetch user permissions on mount and whenever user changes
  useEffect(() => {
    const fetchUserPermissions = async () => {
      if (user) {
        try {
          const { data: userData } = await userService.getUser(user._id);

          setCanSuggestSongs(userData.permissions?.canSuggestSongs || false);
        } catch (error) {
          setCanSuggestSongs(false);
        }
      } else {
        setCanSuggestSongs(false);
      }
    };

    fetchUserPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Fetch the full team song list once, in a single place, so every route has
  // populated `songs` regardless of where the user lands (deep links / reloads).
  const loadSongs = useCallback(async () => {
    const currentUser = auth.getCurrentUser();

    if (!currentUser?.teamId) return;

    try {
      let favoriteSongs = [];

      try {
        const response = await userService.getFavoriteSongs();

        favoriteSongs = response.data || [];
      } catch (error) {
        Sentry.captureException(error, {
          tags: {
            section: 'global-context',
            action: 'fetch-favorite-songs',
          },
          extra: {
            userId: currentUser._id,
            teamId: currentUser.teamId,
            responseStatus: error.response?.status,
            responseData: error.response?.data,
          },
        });
      }

      let songsData = await songService.getSongs(currentUser.teamId);

      // Set favorites
      if (favoriteSongs.length > 0) {
        for (let i = 0; i < favoriteSongs.length; i++) {
          let favSong = songsData.find((s) => s._id === favoriteSongs[i]);

          // If the song was deleted by the admin, remove it from favorites
          if (!favSong) {
            try {
              await userService.removeFavoriteSong(favoriteSongs[i]);
            } catch (ex) {}
          } else {
            favSong.favorite = true;

            const index = songsData.indexOf(favSong);

            songsData.splice(index, 1, favSong);
          }
        }
      }

      // Extract all unique themes from songs
      const themesSet = new Set();

      songsData.forEach((song) => {
        if (song.themes && Array.isArray(song.themes)) {
          song.themes.forEach((theme) => themesSet.add(theme));
        }
      });

      setSongs(songsData);
      setAllThemes(Array.from(themesSet).sort());
    } catch (e) {
      // Swallow: loading state is driven off `dataLoaded`
    }
  }, []);

  // Build the category list (static categories + admin-only extras).
  const populateCategories = useCallback(() => {
    const baseCategories = [
      { _id: 'all', name: 'All', color: 'neutral' },
      { _id: 'favorites', name: 'Favorites', color: 'amber' },
    ];

    const categoriesData = [...baseCategories, ...getCategories()];

    const currentUser = auth.getCurrentUser();

    if (currentUser?.isAdmin) {
      categoriesData.push({ _id: 'hidden', name: 'Hidden', color: 'orange' });
      categoriesData.push({ _id: 'drafts', name: 'Drafts', color: 'pink' });
    }

    categoriesData.push({ _id: 'new', name: 'New', color: 'green' });

    setCategories(categoriesData);
  }, []);

  // Fetch the team's services once, in the same place as songs, so the global
  // loader can wait on a single source of truth.
  const loadServices = useCallback(async () => {
    if (!auth.getCurrentUser()?.teamId) return;

    try {
      let { data: servicesData } = await serviceService.getServices();

      servicesData = sortPastServices(servicesData);

      setServices(servicesData);
    } catch (e) {
      // Swallow: loading state is driven off `servicesLoaded`
    }
  }, []);

  // Run once on mount: if authenticated (has a team), load songs and services
  // in parallel so the global loader can cover the screen until both finish.
  useEffect(() => {
    if (!auth.getCurrentUser()?.teamId) return;

    populateCategories();

    const init = async () => {
      setNetworkErrorHandler(() => {
        setConnectionError(true);
        setLoaderErrorActive(true);
        Sentry.captureMessage('Network error during app initialization', {
          level: 'error',
          tags: { section: 'global-loader' },
        });
      });
      try {
        await Promise.all([
          dataLoaded ? Promise.resolve() : loadSongs().then(() => setDataLoaded(true)),
          servicesLoaded ? Promise.resolve() : loadServices().then(() => setServicesLoaded(true)),
        ]);
      } finally {
        setNetworkErrorHandler(null);
        sessionStorage.removeItem('mg_conn_error_retried');
      }
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleShowPlugins = () => {
    setShowPlugins((prev) => {
      const newState = !prev;
      setSetting(settingKeys.showPlugins, newState);
      return newState;
    });
  };

  const appLoading = !!user?.teamId && (!dataLoaded || !servicesLoaded || connectionError);

  const value = {
    signUpEnabled,
    appLoading,
    connectionError,
    scrollTimerRef,
    isScrolling,
    setIsScrolling,
    scrollingSpeed,
    setScrollingSpeed,
    scrollingPace,
    setScrollingPace,
    showPlugins,
    handleShowPlugins,
    showRemovedFromTeamModal,
    setShowRemovedFromTeamModal,
    canSuggestSongs,
    setCanSuggestSongs,
    songs,
    setSongs,
    services,
    setServices,
    categories,
    setCategories,
    dataLoaded,
    setDataLoaded,
    servicesLoaded,
    setServicesLoaded,
    refetchSongs: loadSongs,
    currentServiceForm,
    setCurrentServiceForm,
    showWelcomeModal,
    setShowWelcomeModal,
    welcomeModalData,
    setWelcomeModalData,
    allThemes,
    setAllThemes,
  };

  return <GlobalContext.Provider value={value}>{children}</GlobalContext.Provider>;
};

export const useGlobalContext = () => {
  const context = useContext(GlobalContext);

  return context;
};
